/**
 * Generic parser for Malvern Zetasizer tab-separated text exports.
 *
 * The parser dynamically discovers array columns (e.g., `Sizes[1] (d.nm)`)
 * and scalar metadata columns from the header row. This makes it work
 * regardless of which fields the user selected for export or which
 * instrument model produced the file.
 *
 * Some exports contain multiple sections (e.g., a summary table followed
 * by a distribution table). The parser detects new header rows and merges
 * corresponding records from each section.
 */
import { parseString } from 'dynamic-typing';
import { ensureString } from 'ensure-string';
/**
 * Parse a Zetasizer tab-separated text export into structured records.
 *
 * The parser dynamically discovers columns from the header row:
 * - Columns matching `Name[N] (units)` are grouped into array data.
 * - All other columns become scalar metadata entries.
 *
 * Files may contain multiple sections with different headers. Each new
 * header row starts a new section, and records are merged across sections
 * in order.
 * @param data - The raw content of the Zetasizer export file (string, ArrayBuffer, or typed array)
 * @returns Array of parsed measurement records
 */
export function fromText(data) {
    const text = ensureString(data);
    const lines = text.split(/\r?\n/).filter((line) => line.trim() !== '');
    if (lines.length < 2) {
        return [];
    }
    const sections = splitSections(lines);
    const sectionResults = [];
    for (const section of sections) {
        const headers = section[0]?.split('\t') ?? [];
        const { arrayGroups, scalarIndices } = classifyColumns(headers);
        const records = [];
        for (let i = 1; i < section.length; i++) {
            const line = section[i];
            if (!line)
                continue;
            const values = line.split('\t');
            const record = { meta: {} };
            for (const group of arrayGroups) {
                record[group.name] = {
                    data: extractFloatArray(values, group.start, group.count),
                    units: group.units,
                };
            }
            const meta = {};
            for (const [name, index] of scalarIndices) {
                const raw = values[index] ?? '';
                if (raw !== '') {
                    meta[name] = parseString(raw);
                }
            }
            record.meta = meta;
            records.push(record);
        }
        sectionResults.push(records);
    }
    return mergeSections(sectionResults);
}
/**
 * Split lines into sections, each starting with a header row.
 *
 * The first line is always treated as a header. Subsequent lines that
 * contain array column patterns (e.g., `Name[1]`) are detected as new
 * header rows, starting a new section.
 * @param lines - Non-empty lines from the file
 * @returns Array of sections, each being an array of lines (header + data)
 */
function splitSections(lines) {
    const sections = [];
    let current = [];
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i] ?? '';
        if (i === 0) {
            current.push(line);
        }
        else if (isHeaderLine(line)) {
            if (current.length > 0) {
                sections.push(current);
            }
            current = [line];
        }
        else {
            current.push(line);
        }
    }
    if (current.length > 0) {
        sections.push(current);
    }
    return sections;
}
const ARRAY_HEADER_PATTERN = /^(?<name>.+?)\[(?:\d+)]\s*(?:\((?<units>.+?)\))?$/;
/**
 * Check if a line looks like a header row by testing whether any
 * tab-separated value matches the array column pattern.
 * @param line - A line from the file
 * @returns True if the line appears to be a header row
 */
function isHeaderLine(line) {
    const fields = line.split('\t');
    return fields.some((field) => ARRAY_HEADER_PATTERN.test(field));
}
/**
 * Scan headers to identify groups of array columns and scalar columns.
 *
 * Array columns follow the pattern `Name[N] (units)` where N is sequential.
 * All other columns are treated as scalar metadata.
 * @param headers - Array of header strings
 * @returns Object with arrayGroups and scalarIndices
 */
function classifyColumns(headers) {
    const arrayGroups = [];
    const scalarIndices = new Map();
    let currentGroup;
    for (let i = 0; i < headers.length; i++) {
        const header = headers[i] ?? '';
        const match = ARRAY_HEADER_PATTERN.exec(header);
        if (match) {
            const name = match.groups?.name ?? '';
            const units = match.groups?.units ?? '';
            if (currentGroup?.name === name) {
                currentGroup.count++;
            }
            else {
                if (currentGroup) {
                    arrayGroups.push(currentGroup);
                }
                currentGroup = { name, units, start: i, count: 1 };
            }
        }
        else {
            if (currentGroup) {
                arrayGroups.push(currentGroup);
                currentGroup = undefined;
            }
            if (header) {
                // For duplicate header names (e.g., "Sample Name" appears twice),
                // keep the last occurrence which is in the metadata section.
                scalarIndices.set(header, i);
            }
        }
    }
    if (currentGroup) {
        arrayGroups.push(currentGroup);
    }
    return { arrayGroups, scalarIndices };
}
/**
 * Merge records from multiple sections by index. Records at the same
 * position across sections are combined, with later sections' metadata
 * and arrays added to earlier ones.
 * @param sectionResults - Array of record arrays, one per section
 * @returns Merged array of records
 */
function mergeSections(sectionResults) {
    if (sectionResults.length === 0)
        return [];
    if (sectionResults.length === 1)
        return sectionResults[0] ?? [];
    const maxLength = Math.max(...sectionResults.map((s) => s.length));
    const merged = [];
    for (let i = 0; i < maxLength; i++) {
        const record = { meta: {} };
        for (const section of sectionResults) {
            const sectionRecord = section[i];
            if (!sectionRecord)
                continue;
            for (const [key, value] of Object.entries(sectionRecord)) {
                if (key === 'meta') {
                    Object.assign(record.meta, value);
                }
                else {
                    record[key] = value;
                }
            }
        }
        merged.push(record);
    }
    return merged;
}
/**
 * Extract a Float64Array from a row of tab-separated values.
 * @param values - Array of string values from the row
 * @param start - Start column index
 * @param count - Number of columns to extract
 * @returns Float64Array of parsed values
 */
function extractFloatArray(values, start, count) {
    const result = new Float64Array(count);
    for (let i = 0; i < count; i++) {
        result[i] = Number(values[start + i]);
    }
    return result;
}
//# sourceMappingURL=fromText.js.map